import { supabase } from "@/lib/supabaseClient";

export interface NotificationEvent {
  id: string;
  kind: "check_in" | "check_out" | "new_reservation" | "rental_start" | "rental_return" | "payment" | "overdue_rental" | "room_dirty";
  title: string;
  detail: string;
  ref: string | null;         // confirmation/agreement code
  ts: string;                  // ISO
  severity: "info" | "success" | "warning" | "error";
}

/** Derive an activity feed from DB events. Sprint 4 — no dedicated
 *  notifications table yet (future: triggers writing to one). Instead
 *  we read recent reservations, rentals, and transactions and fold
 *  them into a single time-sorted feed. */
export async function listRecentActivity(orgId: string, limit: number = 100): Promise<NotificationEvent[]> {
  const [{ data: resv }, { data: rentals }, { data: txns }] = await Promise.all([
    supabase.from("reservations").select("id, confirmation_code, status, check_in_date, check_out_date, checked_in_at, checked_out_at, created_at, primary_guest_id, guest:guests!reservations_primary_guest_id_fkey(full_name_en)")
      .eq("org_id", orgId).order("updated_at", { ascending: false }).limit(limit),
    supabase.from("rentals").select("id, agreement_code, status, start_at, end_at, returned_at, created_at, driver_guest_id, driver:guests!rentals_driver_guest_id_fkey(full_name_en)")
      .eq("org_id", orgId).order("updated_at", { ascending: false }).limit(limit),
    supabase.from("transactions").select("id, type, description, amount, posted_at, guest:guests(full_name_en)")
      .eq("org_id", orgId).in("type", ["payment", "refund"]).order("posted_at", { ascending: false }).limit(limit),
  ]);

  const events: NotificationEvent[] = [];

  (resv ?? []).forEach((r: any) => {
    const g = r.guest?.full_name_en ?? "—";
    events.push({
      id: `resv-created-${r.id}`, kind: "new_reservation",
      title: `New reservation for ${g}`,
      detail: `${r.confirmation_code} · ${r.check_in_date} → ${r.check_out_date}`,
      ref: r.confirmation_code, ts: r.created_at, severity: "info",
    });
    if (r.checked_in_at) {
      events.push({ id: `resv-in-${r.id}`, kind: "check_in",
        title: `${g} checked in`, detail: r.confirmation_code, ref: r.confirmation_code,
        ts: r.checked_in_at, severity: "success" });
    }
    if (r.checked_out_at) {
      events.push({ id: `resv-out-${r.id}`, kind: "check_out",
        title: `${g} checked out`, detail: r.confirmation_code, ref: r.confirmation_code,
        ts: r.checked_out_at, severity: "info" });
    }
  });

  (rentals ?? []).forEach((r: any) => {
    const g = r.driver?.full_name_en ?? "—";
    events.push({ id: `rental-start-${r.id}`, kind: "rental_start",
      title: `Rental started for ${g}`, detail: r.agreement_code, ref: r.agreement_code,
      ts: r.created_at, severity: "info" });
    if (r.returned_at) {
      events.push({ id: `rental-return-${r.id}`, kind: "rental_return",
        title: `${g} returned vehicle`, detail: r.agreement_code, ref: r.agreement_code,
        ts: r.returned_at, severity: "success" });
    }
    if (r.status === "active" && new Date(r.end_at) < new Date()) {
      events.push({ id: `rental-overdue-${r.id}`, kind: "overdue_rental",
        title: `Rental overdue: ${g}`, detail: `${r.agreement_code} — due ${new Date(r.end_at).toLocaleString()}`,
        ref: r.agreement_code, ts: r.end_at, severity: "warning" });
    }
  });

  (txns ?? []).forEach((t: any) => {
    const g = t.guest?.full_name_en ?? "—";
    events.push({
      id: `txn-${t.id}`, kind: "payment",
      title: t.type === "refund" ? `Refund to ${g}` : `Payment from ${g}`,
      detail: `${Number(t.amount).toFixed(3)} OMR · ${t.description}`,
      ref: null, ts: t.posted_at,
      severity: t.type === "refund" ? "error" : "success",
    });
  });

  return events.sort((a, b) => (b.ts > a.ts ? 1 : -1)).slice(0, limit);
}
